import classNames from "classnames";
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import ListGroup from "reactstrap/es/ListGroup";
import ListGroupItem from "reactstrap/es/ListGroupItem";
import { isIterableArray } from "../../helpers/utils";
import FalconCardHeader from "../common/FalconCardHeader";
import Notification from "../notification/Notification";
import Avatar from "../common/Avatar";
import {
  fetchNotifications,
  markNotificationsAsRead,
} from "../../helpers/api/notifications";
import { NotificationContext } from "../../context/Context";
import moment from "moment";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { useHistory } from "react-router-dom";
import { Message } from "../UI/preloading/PreloadState";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import {
  buildRewardsNotificationContent,
  getRewardsNotificationView,
  isRewardsNotification,
} from "../rewards/rewardsNotifications";
import { REWARDS_ENABLED } from "../../helpers/rewardsEnabled";

const NotificationDropdown = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, setNotifications } = useContext(NotificationContext);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const handleToggle = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const findNotifications = () => {
    setLoading(true);
    fetchNotifications()
      .then((response) => {
        const { data } = response;
        setNotifications(data);
      })
      .catch((error) => {
        handleErrorMessage(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    findNotifications();
  }, []);

  const markAsReadAll = () => {
    setSubmitting(true);
    markNotificationsAsRead()
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          findNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const markAsRead = (type, id, typeId, params = null) => {
    setSubmitting(true);
    markNotificationsAsRead(id)
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          findNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSubmitting(false);
        history.push({
          // HO-TASK-290: las de Homelan Rewards llevan a la ruta que define
          // su tipo (la vista de Rewards o, si la racha está en riesgo, a pagar).
          pathname: isRewardsNotification(type)
            ? params?.route
            : type === "voting"
              ? `/dashboard/surveys/${typeId}`
              : type === "new-complaint" || type === "complaint-closed"
              ? `/dashboard/complaints/${typeId}`
              : type === "reserved-common-area"
              ? `/dashboard/areas/${typeId}/reservations`
              : type === "new-debts-to-pay"
              ? `/dashboard/my/finance/topay/${typeId}`
              : type === "property-payment"
              ? `/dashboard/finance/${params?.condominium}/payments/${params?.id}`
              : type === "new-ticket"
              ? `/root/support/${params?.id}`
              : type === "response-ticket"
              ? `/dashboard/support/${params?.id}`
              : "",
          state: type === "new-debts-to-pay" ? params : null,
        });
      });
  };

  return (
    <Dropdown
      nav
      inNavbar
      isOpen={isOpen}
      toggle={handleToggle}
      onMouseOver={() => {
        const windowWidth = window.innerWidth;
        windowWidth > 992 && setIsOpen(true);
      }}
      onMouseLeave={() => {
        const windowWidth = window.innerWidth;
        windowWidth > 992 && setIsOpen(false);
      }}
    >
      <DropdownToggle
        nav
        className={classNames("px-0 mr-2", {
          "notification-indicator notification-indicator-primary":
            isIterableArray(notifications),
        })}
      >
        <Avatar
          src={"/assets/img/icons/Icono campana A 2.png"}
          shadow={true}
          rounded="circle"
          size="s"
          className="fs-4"
        />
      </DropdownToggle>
      <DropdownMenu right className="dropdown-menu-card">
        <Card
          className="card-notification shadow-none"
          style={{ maxWidth: "20rem" }}
        >
          <FalconCardHeader
            className="card-header"
            title="Notificaciones"
            titleTag="h6"
            light={false}
          >
            <Button
              color="link"
              className="card-link font-weight-normal"
              onClick={markAsReadAll}
            >
              {submitting && (
                <Spinner size="sm" color="primary" className="mr-2" />
              )}
              Marcar todas como leídas
            </Button>
          </FalconCardHeader>
          <ListGroup flush className="font-weight-normal fs--1">
            <div className="list-group-title">Nuevas</div>
            {!loading ? (
              isIterableArray(notifications) ? (
                notifications.map((notification, index) => {
                  if (!REWARDS_ENABLED && isRewardsNotification(notification.type)) return null;
                  const data = JSON.parse(notification.data);
                  const rewards = getRewardsNotificationView(notification);
                  if (rewards) {
                    return (
                      <ListGroupItem key={index} onClick={handleToggle}>
                        <Notification
                          emoji={rewards.emoji}
                          time={moment(notification.created_at).fromNow()}
                          onClick={() =>
                            markAsRead(notification.type, notification.id, null, {
                              route: rewards.route,
                            })
                          }
                          flush
                          unread={!notification.read_at}
                          content={buildRewardsNotificationContent(rewards)}
                        />
                      </ListGroupItem>
                    );
                  }
                  return (
                    <ListGroupItem key={index} onClick={handleToggle}>
                      <Notification
                        emoji={"🔔"}
                        time={moment(notification.created_at).fromNow()}
                        onClick={() =>
                          markAsRead(
                            notification.type,
                            notification.id,
                            notification.type === "reserved-common-area"
                              ? data.data.common_area.id
                              : data?.data?.id,
                            notification.type === "new-debts-to-pay"
                              ? {
                                  amount: data.data.amount,
                                  date: data.data.created_at,
                                  propertyDebtId: data.data.id,
                                  aliquot: data.data.aliquot,
                                  paymented: false,
                                  total_paid: 0,
                                }
                              : notification.type === "property-payment"
                              ? {
                                  condominium:
                                    data.data.property_debts.property
                                      .condominium.id,
                                  id: data.data.id,
                                }
                              : notification.type === "new-ticket"
                              ? { id: data.data.id }
                              : notification.type === "response-ticket"
                              ? { id: data.data.ticket_id }
                              : null
                          )
                        }
                        flush
                        unread={!notification.read_at}
                        content={`${data?.message} ${
                          data?.data?.title ? data?.data?.title : ""
                        }`}
                      />
                    </ListGroupItem>
                  );
                })
              ) : (
                <Message
                  color="info"
                  icon={faInfoCircle}
                  className="py-3 fs--1"
                >
                  No tienes notificaciones nuevas
                </Message>
              )
            ) : (
              <ListGroupItem className="text-center py-3">
                <Spinner size="sm" color="primary" />
              </ListGroupItem>
            )}
          </ListGroup>
          <button
            className="card-footer text-center border-top-0"
            onClick={handleToggle}
          >
            <Link
              className="card-link d-block"
              to={
                user?.role !== "root"
                  ? "/dashboard/notifications"
                  : "/root/notifications"
              }
            >
              Ver todas
            </Link>
          </button>
        </Card>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationDropdown;
